// apps/patient/src/components/messages/ThreadFilters.tsx

import { X } from 'lucide-react';
import { cn } from '../../lib/utils';

export default function ThreadFilters({ 
  filters, 
  onFilterChange,
  simplified = true // Patient-specific simplified filters
}) {
  // Patient-appropriate categories
  const categoryOptions = [
    { value: 'general', label: 'General' },
    { value: 'clinical', label: 'Clinical' },
    { value: 'administrative', label: 'Administrative' },
    { value: 'billing', label: 'Billing' },
    { value: 'prescription', label: 'Prescription' },
    { value: 'appointment', label: 'Appointment' },
    { value: 'lab_results', label: 'Lab Results' }
  ];
  
  const statusOptions = [
    { value: 'open', label: 'Open' },
    { value: 'closed', label: 'Closed' },
    { value: 'awaiting_response', label: 'Awaiting Response' }
  ];
  
  const handleCategoryToggle = (category) => {
    const current = filters.category || [];
    const updated = current.includes(category)
      ? current.filter(c => c !== category)
      : [...current, category];
    onFilterChange('category', updated);
  };
  
  const handleStatusToggle = (status) => {
    const current = filters.status || [];
    const updated = current.includes(status)
      ? current.filter(s => s !== status)
      : [...current, status];
    onFilterChange('status', updated);
  };
  
  const clearFilters = () => {
    onFilterChange('category', []);
    onFilterChange('status', []);
    onFilterChange('unread_only', false);
    onFilterChange('has_attachments', false);
  };
  
  const hasActiveFilters = filters.category?.length > 0 || 
                           filters.status?.length > 0 || 
                           filters.unread_only || 
                           filters.has_attachments;
  
  return (
    <div className="border-b bg-gray-50 p-4 space-y-4">
      {/* Categories */}
      <div>
        <label className="text-xs font-medium text-gray-700 uppercase tracking-wider mb-2 block">
          Category
        </label>
        <div className="flex flex-wrap gap-2">
          {categoryOptions.map(option => (
            <button
              key={option.value}
              onClick={() => handleCategoryToggle(option.value)}
              className={cn(
                "px-3 py-1 text-xs rounded-full border transition-colors",
                filters.category?.includes(option.value)
                  ? "bg-primary text-white border-primary"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      
      {/* Status */}
      <div>
        <label className="text-xs font-medium text-gray-700 uppercase tracking-wider mb-2 block">
          Status
        </label>
        <div className="flex flex-wrap gap-2">
          {statusOptions.map(option => (
            <button
              key={option.value}
              onClick={() => handleStatusToggle(option.value)}
              className={cn(
                "px-3 py-1 text-xs rounded-full border transition-colors",
                filters.status?.includes(option.value)
                  ? "bg-primary text-white border-primary"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>
      
      {/* Quick Filters */}
      <div>
        <label className="text-xs font-medium text-gray-700 uppercase tracking-wider mb-2 block">
          Quick Filters
        </label>
        <div className="space-y-2">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.unread_only}
              onChange={(e) => onFilterChange('unread_only', e.target.checked)}
              className="rounded border-gray-300 text-primary focus:ring-primary"
            />
            <span className="text-sm text-gray-700">Unread only</span>
          </label>
          
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.has_attachments}
              onChange={(e) => onFilterChange('has_attachments', e.target.checked)}
              className="rounded border-gray-300 text-primary focus:ring-primary"
            />
            <span className="text-sm text-gray-700">Has attachments</span>
          </label>
        </div>
      </div>
      
      {/* Clear Filters */}
      {hasActiveFilters && (
        <button
          onClick={clearFilters}
          className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
        >
          <X className="h-4 w-4" />
          Clear all filters
        </button>
      )}
    </div>
  );
}